import React, { useEffect } from 'react';
import { Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { SimpleModal } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { dateToFormat } from 'src/utils/date';
import { formatCouponDiscount, formatCouponDuration, groupRedemptionsByMonth } from '../couponsModel';
import useStyles from './styles';
const EMPTY_LIST = [];

/**
 * Who has been given one coupon, and how often. Opened from the Redeemed count in the coupons
 * table; the count answers "how many", this answers "by whom and when".
 */
export const CouponRedemptionsModal = observer(_ref => {
  var _coupon$id, _store$stripe$couponR, _coupon$code;
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    coupon = _ref.coupon;
  const classes = useStyles();
  const store = useStore();
  const couponId = (_coupon$id = coupon === null || coupon === void 0 ? void 0 : coupon.id) !== null && _coupon$id !== void 0 ? _coupon$id : '';
  const redemptions = (_store$stripe$couponR = store.stripe.couponRedemptionsList) !== null && _store$stripe$couponR !== void 0 ? _store$stripe$couponR : EMPTY_LIST;
  const isLoading = store.stripe.getCouponRedemptionsAsync.inProgress;
  const isError = store.stripe.getCouponRedemptionsAsync.isError;
  useEffect(() => {
    if (isOpen && couponId) {
      store.stripe.getCouponRedemptionsAsync.run({
        couponId
      });
    } else {
      store.stripe.clearCouponRedemptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, couponId]);
  const byMonth = groupRedemptionsByMonth(redemptions);

  // The list is empty while it loads and after a failed load, so only a loaded list can be counted.
  // Otherwise fall back to the coupon's own count — the number Ops just clicked — and never show 0.
  const total = !isLoading && !isError ? redemptions.length : coupon === null || coupon === void 0 ? void 0 : coupon.redemptionsCount;

  /** An account-wide redemption covers everything the account is billed for. */
  const describeTarget = row => row.propertyAddress || (row.propertyId ? 'Property' : 'Whole account');
  const renderBody = () => {
    if (isLoading) {
      return /*#__PURE__*/React.createElement(Box, {
        className: classes.centered
      }, /*#__PURE__*/React.createElement(CircularProgress, {
        size: 24
      }));
    }
    if (isError) {
      return /*#__PURE__*/React.createElement(Box, {
        className: classes.centered
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1"
      }, "Could not load the redemptions for this coupon."));
    }
    if (redemptions.length === 0) {
      return /*#__PURE__*/React.createElement(Box, {
        className: classes.centered
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1"
      }, "This coupon has not been redeemed yet."));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      className: classes.monthRow
    }, byMonth.map(month => /*#__PURE__*/React.createElement(Chip, {
      key: month.key,
      label: "".concat(month.label, ": ").concat(month.count),
      size: "small",
      className: classes.monthChip
    }))), /*#__PURE__*/React.createElement(Table, {
      size: "small"
    }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
      className: classes.headCell
    }, "Account"), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.headCell
    }, "Applies to"), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.headCell
    }, "Redeemed"), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.headCell
    }, "Discount runs until"))), /*#__PURE__*/React.createElement(TableBody, null, redemptions.map(row => /*#__PURE__*/React.createElement(TableRow, {
      key: row.id
    }, /*#__PURE__*/React.createElement(TableCell, null, row.companyName || row.companyId), /*#__PURE__*/React.createElement(TableCell, null, describeTarget(row)), /*#__PURE__*/React.createElement(TableCell, null, row.redeemedOn ? dateToFormat(row.redeemedOn) : '—'), /*#__PURE__*/React.createElement(TableCell, null, row.expiresOn ? dateToFormat(row.expiresOn) : 'Until revoked', row.isExpired && /*#__PURE__*/React.createElement(Chip, {
      label: "Ended",
      size: "small",
      className: classes.endedChip
    })))))));
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: null,
    isOpen: isOpen,
    handleClose: onClose,
    title: /*#__PURE__*/React.createElement(Box, {
      component: "span",
      className: classes.titleRow
    }, "Redemptions \u2014 ".concat((_coupon$code = coupon === null || coupon === void 0 ? void 0 : coupon.code) !== null && _coupon$code !== void 0 ? _coupon$code : ''), coupon && /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      className: classes.subtitle
    }, "".concat(formatCouponDiscount(coupon), " \xB7 ").concat(formatCouponDuration(coupon)))),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: onClose
    }, "Close")
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.content
  }, total != null && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    className: classes.total
  }, "Redeemed ".concat(total, " ").concat(total === 1 ? 'time' : 'times', " in total")), renderBody()));
});